// src/auth/ProtectedRoute.tsx
// Updated: 2026-08-03 15:20:00 CDT
// Purpose: Fail-closed route guard that requires both Supabase authentication
// and membership in the server-side admin_users allow-list.

import React, { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/useAuth";
import { supabase } from "@/lib/supabaseClient";

type AccessState = "idle" | "checking" | "authorized" | "denied";

const ProtectedRoute: React.FC = () => {
  const { user, isLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [accessState, setAccessState] = useState<AccessState>("idle");

  useEffect(() => {
    let active = true;

    if (isLoading) {
      return () => {
        active = false;
      };
    }

    if (!user) {
      setAccessState("idle");
      return () => {
        active = false;
      };
    }

    const verifyAdministrator = async () => {
      setAccessState("checking");
      console.debug("[ProtectedRoute] Verifying administrator access", {
        userId: user.id,
        pathname: location.pathname,
      });

      try {
        const { data, error } = await supabase.functions.invoke("admin-access", {
          method: "GET",
        });

        if (!active) return;

        if (error || data?.ok !== true) {
          console.warn("[ProtectedRoute] Administrator access denied", {
            userId: user.id,
            functionError: error?.message ?? null,
            response: data ?? null,
          });

          setAccessState("denied");
          await supabase.auth.signOut();

          navigate("/login", {
            replace: true,
            state: {
              accessDenied: true,
              message: "This account is not authorized to use the administration console.",
            },
          });
          return;
        }

        console.debug("[ProtectedRoute] Administrator access confirmed", {
          userId: user.id,
          pathname: location.pathname,
        });
        setAccessState("authorized");
      } catch (error) {
        if (!active) return;

        console.error("[ProtectedRoute] Administrator verification failed", {
          userId: user.id,
          error,
        });

        setAccessState("denied");
        await supabase.auth.signOut();

        navigate("/login", {
          replace: true,
          state: {
            accessDenied: true,
            message: "Administrator access could not be verified. Please sign in again.",
          },
        });
      }
    };

    void verifyAdministrator();

    return () => {
      active = false;
    };
  }, [isLoading, location.pathname, navigate, user]);

  if (isLoading || (user && accessState !== "authorized")) {
    console.debug("[ProtectedRoute] Waiting for access verification", {
      pathname: location.pathname,
      isLoading,
      accessState,
    });
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="text-sm text-gray-500" role="status">
          Checking administrator access…
        </div>
      </div>
    );
  }

  if (!user) {
    console.warn("[ProtectedRoute] No user. Redirecting to /login from:", location.pathname);
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  console.debug("[ProtectedRoute] Authorized. Rendering protected content:", location.pathname);
  return <Outlet />;
};

export default ProtectedRoute;
